import { getCurrencyByCode } from '@/constants/currencies'
import { fromCents, toCents } from '@lib/money'
import { getInitials, getShortName } from '@/utils/userNames'
import CurrencySelect from '@components/CurrencySelect'
import Modal from '@components/Modal'
import { useImageUpload } from '@hooks/useImageUpload'
import { GetUserType } from '@orbit/api-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Breadcrumb, BreadcrumbSeparator, BreadcrumbStep } from '../Stepper.styles'
import {
	AmountInput,
	AmountInputWrapper,
	AmountPrefix,
	BackButton,
	Button,
	ButtonGroup,
	FormGroup,
	FormLabel,
	HiddenInput,
	ImageActionButton,
	ImageActions,
	ImageOverlay,
	ImagePlaceholder,
	ImagePreview,
	ImageSection,
	MemberList,
	MemberName,
	MemberRow,
	PayerRecipientArrow,
	PayerRecipientLabel,
	PayerRecipientName,
	PayerRecipientPerson,
	PayerRecipientRow,
	ProfileAvatar,
	SoftWarning,
	StepHeading,
	StepIndicator,
	ThirdPartyNotice,
	ValidationError,
} from './AddPayUpModal.styles'

const SOFT_WARN_THRESHOLD_CENTS = 100_000_00

export interface AddPayUpPrefill {
	fromUserId: number
	toUserId: number
	amount: number
	currencyCode: string
}

export interface AddPayUpData {
	fromUserId: number
	toUserId: number
	amount: number
	currencyCode: string
	imageFile?: File
}

interface AddPayUpModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (data: AddPayUpData) => void
	members: GetUserType[]
	currentUserId: number
	defaultCurrencyCode: string
	prefill?: AddPayUpPrefill
	isSubmitting?: boolean
}

const getMemberDisplayName = (m: GetUserType, currentUserId: number): string => {
	const short = getShortName(m)
	return m.id === currentUserId ? `${short} (You)` : short
}

const sortMembersWithCurrentFirst = (members: GetUserType[], currentUserId: number): GetUserType[] => {
	return [...members].sort((a, b) => {
		if (a.id === currentUserId) return -1
		if (b.id === currentUserId) return 1
		return 0
	})
}

const MemberAvatar: React.FC<{ member: GetUserType }> = ({ member }) => (
	<ProfileAvatar>
		{member.profilePicture ? (
			<img src={member.profilePicture} alt={getInitials(member)} />
		) : (
			getInitials(member)
		)}
	</ProfileAvatar>
)

const AddPayUpModal: React.FC<AddPayUpModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	members,
	currentUserId,
	defaultCurrencyCode,
	prefill,
	isSubmitting = false,
}) => {
	const [step, setStep] = useState<1 | 2 | 3>(1)
	const [fromUserId, setFromUserId] = useState<number | null>(null)
	const [toUserId, setToUserId] = useState<number | null>(null)
	const [amount, setAmount] = useState('')
	const [currencyCode, setCurrencyCode] = useState(defaultCurrencyCode)
	const {
		imageFile,
		previewUrl: imagePreviewUrl,
		showOverlay: showImageOverlay,
		setShowOverlay: setShowImageOverlay,
		fileInputRef,
		handleImageSelect,
		handleImageRemove,
	} = useImageUpload({ isOpen })

	const isTwoMemberTrip = members.length === 2

	useEffect(() => {
		if (!isOpen) return

		if (prefill) {
			setStep(3)
			setFromUserId(prefill.fromUserId)
			setToUserId(prefill.toUserId)
			setAmount(prefill.amount > 0 ? fromCents(prefill.amount) : '')
			setCurrencyCode(prefill.currencyCode)
		} else if (isTwoMemberTrip) {
			const otherMember = members.find(m => m.id !== currentUserId)
			setStep(2)
			setFromUserId(currentUserId)
			setToUserId(otherMember?.id ?? null)
			setAmount('')
			setCurrencyCode(defaultCurrencyCode)
		} else {
			setStep(1)
			setFromUserId(null)
			setToUserId(null)
			setAmount('')
			setCurrencyCode(defaultCurrencyCode)
		}
	}, [isOpen, prefill, isTwoMemberTrip, members, currentUserId, defaultCurrencyCode])

	const sortedMembers = useMemo(
		() => sortMembersWithCurrentFirst(members, currentUserId),
		[members, currentUserId],
	)

	const recipientCandidates = useMemo(
		() => sortedMembers.filter(m => m.id !== fromUserId),
		[sortedMembers, fromUserId],
	)

	const fromMember = members.find(m => m.id === fromUserId) ?? null
	const toMember = members.find(m => m.id === toUserId) ?? null

	const amountCents = toCents(amount)
	const currencyInfo = getCurrencyByCode(currencyCode)
	const symbol = currencyInfo?.symbol ?? '$'
	const showSoftWarning = amountCents > SOFT_WARN_THRESHOLD_CENTS

	const validationError = (() => {
		if (amountCents <= 0) return 'Amount must be greater than zero'
		if (fromUserId == null || toUserId == null) return 'Payer and recipient are required'
		if (fromUserId === toUserId) return 'Payer and recipient must be different'
		return null
	})()

	const canSubmit = !validationError && !isSubmitting

	const handlePayerSelect = (userId: number) => {
		setFromUserId(userId)
		if (toUserId === userId) setToUserId(null)
		setStep(2)
	}

	const handleRecipientSelect = (userId: number) => {
		setToUserId(userId)
		setStep(3)
	}

	const handleBack = () => {
		if (step === 3) {
			if (isTwoMemberTrip || prefill) {
				onClose()
			} else {
				setStep(2)
			}
		} else if (step === 2) {
			setStep(1)
		}
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!canSubmit || fromUserId == null || toUserId == null) return
		onSubmit({
			fromUserId,
			toUserId,
			amount: amountCents,
			currencyCode,
			imageFile: imageFile ?? undefined,
		})
	}

	const showBackButton =
		step !== 1 && !(step === 2 && isTwoMemberTrip) && !(step === 3 && (isTwoMemberTrip || prefill))

	const showBreadcrumb = !prefill && !isTwoMemberTrip

	const formId = 'add-payup-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Record a Payup"
			size="medium"
			backgroundColor="#fff8f6"
			footer={
				step === 3 ? (
					<ButtonGroup>
						<Button type="button" $variant="secondary" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" form={formId} $variant="primary" disabled={!canSubmit}>
							{isSubmitting ? 'Recording...' : 'Confirm Payup'}
						</Button>
					</ButtonGroup>
				) : null
			}
		>
			{showBreadcrumb && (
				<Breadcrumb>
					<BreadcrumbStep
						type="button"
						$current={step === 1}
						disabled={step === 1}
						onClick={() => setStep(1)}
					>
						From
					</BreadcrumbStep>
					<BreadcrumbSeparator>›</BreadcrumbSeparator>
					<BreadcrumbStep
						type="button"
						$current={step === 2}
						disabled={step <= 2}
						onClick={() => setStep(2)}
					>
						To
					</BreadcrumbStep>
					<BreadcrumbSeparator>›</BreadcrumbSeparator>
					<BreadcrumbStep type="button" $current={step === 3} disabled>
						Details
					</BreadcrumbStep>
				</Breadcrumb>
			)}

			{step === 1 && (
				<>
					<StepIndicator>Step 1 of 3</StepIndicator>
					<StepHeading>Who paid?</StepHeading>
					<MemberList>
						{sortedMembers.map(member => (
							<MemberRow
								key={member.id}
								type="button"
								onClick={() => handlePayerSelect(member.id)}
							>
								<MemberAvatar member={member} />
								<MemberName>{getMemberDisplayName(member, currentUserId)}</MemberName>
							</MemberRow>
						))}
					</MemberList>
				</>
			)}

			{step === 2 && (
				<>
					{showBackButton && (
						<BackButton type="button" onClick={handleBack}>
							← Back
						</BackButton>
					)}
					<StepIndicator>
						Step {isTwoMemberTrip ? '1' : '2'} of {isTwoMemberTrip ? '2' : '3'}
					</StepIndicator>
					<StepHeading>Who received the payment?</StepHeading>
					<MemberList>
						{recipientCandidates.map(member => (
							<MemberRow
								key={member.id}
								type="button"
								onClick={() => handleRecipientSelect(member.id)}
							>
								<MemberAvatar member={member} />
								<MemberName>{getMemberDisplayName(member, currentUserId)}</MemberName>
							</MemberRow>
						))}
					</MemberList>
				</>
			)}

			{step === 3 && fromMember && toMember && (
				<form id={formId} onSubmit={handleSubmit}>
					{showBackButton && (
						<BackButton type="button" onClick={handleBack}>
							← Back
						</BackButton>
					)}
					{!prefill && (
						<StepIndicator>{isTwoMemberTrip ? 'Step 2 of 2' : 'Step 3 of 3'}</StepIndicator>
					)}

					<PayerRecipientRow>
						<PayerRecipientPerson>
							<PayerRecipientLabel>From</PayerRecipientLabel>
							<MemberAvatar member={fromMember} />
							<PayerRecipientName>
								{getMemberDisplayName(fromMember, currentUserId)}
							</PayerRecipientName>
						</PayerRecipientPerson>
						<PayerRecipientArrow>→</PayerRecipientArrow>
						<PayerRecipientPerson>
							<PayerRecipientLabel>To</PayerRecipientLabel>
							<MemberAvatar member={toMember} />
							<PayerRecipientName>
								{getMemberDisplayName(toMember, currentUserId)}
							</PayerRecipientName>
						</PayerRecipientPerson>
					</PayerRecipientRow>

					<FormGroup>
						<FormLabel htmlFor="payup-amount">Amount</FormLabel>
						<AmountInputWrapper>
							<AmountPrefix>{symbol}</AmountPrefix>
							<AmountInput
								id="payup-amount"
								type="number"
								value={amount}
								onChange={e => setAmount(e.target.value)}
								placeholder="0.00"
								min="0.01"
								step="0.01"
								autoFocus
							/>
						</AmountInputWrapper>
						{showSoftWarning && <SoftWarning>That seems high — please confirm.</SoftWarning>}
					</FormGroup>

					<FormGroup>
						<CurrencySelect
							value={currencyCode}
							onChange={setCurrencyCode}
							label="Currency"
							htmlFor="payup-currency"
						/>
					</FormGroup>

					<FormGroup>
						<FormLabel>Receipt (optional)</FormLabel>
						<HiddenInput
							ref={fileInputRef}
							type="file"
							accept="image/*"
							onChange={handleImageSelect}
						/>
						<ImageSection>
							{imagePreviewUrl ? (
								<>
									<ImagePreview onClick={() => setShowImageOverlay(true)}>
										<img src={imagePreviewUrl} alt="Receipt" />
									</ImagePreview>
									<ImageActions>
										<ImageActionButton
											type="button"
											onClick={() => fileInputRef.current?.click()}
										>
											Replace
										</ImageActionButton>
										<ImageActionButton type="button" $danger onClick={handleImageRemove}>
											Remove
										</ImageActionButton>
									</ImageActions>
								</>
							) : (
								<ImagePlaceholder onClick={() => fileInputRef.current?.click()}>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
										<circle cx="8.5" cy="8.5" r="1.5" />
										<polyline points="21 15 16 10 5 21" />
									</svg>
								</ImagePlaceholder>
							)}
						</ImageSection>
					</FormGroup>

					{validationError && amount !== '' && <ValidationError>{validationError}</ValidationError>}

					{fromUserId !== currentUserId && toUserId !== currentUserId && (
						<ThirdPartyNotice>
							*You&apos;re recording a payup between {getShortName(fromMember)} and{' '}
							{getShortName(toMember)} on their behalf.
						</ThirdPartyNotice>
					)}

					{showImageOverlay && imagePreviewUrl && (
						<ImageOverlay onClick={() => setShowImageOverlay(false)}>
							<img src={imagePreviewUrl} alt="Receipt full size" />
						</ImageOverlay>
					)}
				</form>
			)}
		</Modal>
	)
}

export default AddPayUpModal
